import { useCallback } from 'react';
import { useSweepApi } from '../sweep/useSweepApi';
import type { UserInputsByMetadataId } from '@server/user-inputs';
import type { ConfigurationCommentDto } from '../../components/documentation/salesforce-documentation-card/annotations/comments/types';
import type {
  DocumentationTagAndMetadataElementsDto,
  DocumentationUserAndMetadataElementsDto,
} from '../../types/documentationUserInputs';

interface PatchCommentProps {
  commentId: string;
  commentBody?: string;
  mentionedUserIds?: string[];
  properties?: { pinned: boolean };
  metadataId: string;
  crmOrgId: string;
}

const DOCUMENTATION_SALESFORCE_USER_INPUTS = '/documentation-salesforce/user-inputs';

export const useUserInputsApiFacade = () => {
  const sweepApi = useSweepApi();

  return {
    get_tags: useCallback(
      async (crmOrgId: string) => {
        const response = await sweepApi.get<DocumentationTagAndMetadataElementsDto[]>(
          `${DOCUMENTATION_SALESFORCE_USER_INPUTS}/tags?crmOrgId=${crmOrgId}`,
        );
        return response.data;
      },
      [sweepApi],
    ),

    post_tag: useCallback(
      async (tag: { name: string; color: string }) => {
        const response = await sweepApi.post<DocumentationTagAndMetadataElementsDto>(
          `${DOCUMENTATION_SALESFORCE_USER_INPUTS}/tags`,
          tag,
        );
        return response.data;
      },
      [sweepApi],
    ),

    patch_tag: useCallback(
      async (tag: { id: string; name?: string; color?: string }) => {
        await sweepApi.patch<void>(`${DOCUMENTATION_SALESFORCE_USER_INPUTS}/tags/${tag.id}`, tag);
      },
      [sweepApi],
    ),

    delete_tag: useCallback(
      async (tagId: string) => {
        const response = await sweepApi.delete<void>(
          `${DOCUMENTATION_SALESFORCE_USER_INPUTS}/tags/${tagId}`,
        );
        return response.data;
      },
      [sweepApi],
    ),

    get_users: useCallback(
      async (crmOrgId: string) => {
        const response = await sweepApi.get<DocumentationUserAndMetadataElementsDto[]>(
          `${DOCUMENTATION_SALESFORCE_USER_INPUTS}/users?crmOrgId=${crmOrgId}`,
        );
        return response.data;
      },
      [sweepApi],
    ),

    delete_userConfigurations: useCallback(
      async (userId: string) => {
        const response = await sweepApi.delete<void>(
          `${DOCUMENTATION_SALESFORCE_USER_INPUTS}/users/${userId}`,
        );
        return response.data;
      },
      [sweepApi],
    ),

    put_userInputsForConfiguration: useCallback(
      async ({
        crmOrgId,
        userInputs,
      }: {
        crmOrgId: string;
        userInputs: UserInputsByMetadataId;
      }) => {
        const [[metadataId, userInputData]] = Object.entries(userInputs);
        await sweepApi.put<void>(
          `${DOCUMENTATION_SALESFORCE_USER_INPUTS}/${metadataId}?crmOrgId=${crmOrgId}`,
          userInputData,
        );
      },
      [sweepApi],
    ),

    get_configurationComments: useCallback(
      async (metadataId: string, crmOrgId: string) => {
        const response = await sweepApi.get<ConfigurationCommentDto[]>(
          `${DOCUMENTATION_SALESFORCE_USER_INPUTS}/${metadataId}/comments?crmOrgId=${crmOrgId}`,
        );
        return response.data;
      },
      [sweepApi],
    ),

    post_configurationComment: useCallback(
      async (
        comment: Pick<ConfigurationCommentDto, 'commentBody' & 'mentionedUserIds' & 'properties'>,
        metadataId: string,
        crmOrgId: string,
      ) => {
        const response = await sweepApi.post<ConfigurationCommentDto>(
          `${DOCUMENTATION_SALESFORCE_USER_INPUTS}/${metadataId}/comments?crmOrgId=${crmOrgId}`,
          comment,
        );
        return response.data;
      },
      [sweepApi],
    ),

    patch_configurationComment: useCallback(
      async ({
        commentId,
        commentBody,
        mentionedUserIds,
        properties,
        metadataId,
        crmOrgId,
      }: PatchCommentProps) => {
        await sweepApi.patch<void>(
          `${DOCUMENTATION_SALESFORCE_USER_INPUTS}/${metadataId}/comments/${commentId}?crmOrgId=${crmOrgId}`,
          { commentBody, mentionedUserIds, properties },
        );
      },
      [sweepApi],
    ),

    delete_configurationComment: useCallback(
      async (commentId: string, metadataId: string, crmOrgId: string) => {
        await sweepApi.delete<void>(
          `${DOCUMENTATION_SALESFORCE_USER_INPUTS}/${metadataId}/comments/${commentId}?crmOrgId=${crmOrgId}`,
        );
      },
      [sweepApi],
    ),
  };
};
